import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Button, Field, Input, Wordmark } from '@glob/ui';
import { GlobVector } from '@glob/glob';
import { useT, type MessageKey } from '../../i18n';
import { useVaultUnlock, type UnlockStage } from '../../features/auth/useVaultUnlock';
import { PassphraseInput } from '../../features/auth/passphrase-input';
import { RecoverySeedInput, seedFromCells } from '../../features/auth/recovery-seed-input';

// Remember the last email used to unlock so returning users don't retype it.
// Email isn't a secret (it's already in autocomplete); persisting it is purely
// a convenience and never touches key material.
const LAST_EMAIL_KEY = 'glob.last-email';
function readLastEmail(): string {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}
function saveLastEmail(email: string): void {
  try {
    localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    // Best-effort; private mode just means it won't prefill next time.
  }
}

const STAGE_KEY: Record<UnlockStage, MessageKey> = {
  'requesting-options': 'unlock.stage.requesting-options',
  authenticating: 'unlock.stage.authenticating',
  'awaiting-passphrase': 'unlock.stage.awaiting-passphrase',
  'awaiting-recovery': 'unlock.stage.awaiting-recovery',
  deriving: 'unlock.stage.deriving',
  unlocking: 'unlock.stage.unlocking',
  unlocked: 'unlock.stage.unlocked',
};

function UnlockFlow() {
  const t = useT();
  const navigate = useNavigate();
  const { unlock, submitPassphrase, submitRecoverySeed, reset } = useVaultUnlock();

  // Clear the module-scope pending context + vaultStore.status on unmount so
  // that navigating away mid-unlock (e.g. clicking "Create account" after
  // entering email) doesn't leave the store stuck in 'unlocking' or strand a
  // stale verify context. The reset is idempotent and cheap.
  useEffect(() => () => reset(), [reset]);

  const [email, setEmail] = useState(readLastEmail);
  const [stage, setStage] = useState<UnlockStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'email' | 'passphrase' | 'recovery'>('email');
  const [passphrase, setPassphrase] = useState('');
  const [seedCells, setSeedCells] = useState<string[]>(Array(24).fill(''));
  const [newPassphrase, setNewPassphrase] = useState('');
  const [newPassphraseOk, setNewPassphraseOk] = useState(false);

  const busy = stage !== null && stage !== 'unlocked';

  const reportError = useCallback((err: unknown) => {
    setError(err instanceof Error ? err.message : 'unlock failed');
    setStage(null);
  }, []);

  const startUnlock = useCallback(async () => {
    setError(null);
    saveLastEmail(email.trim());
    setStage('requesting-options');
    try {
      const result = await unlock({ email, onStage: setStage });
      if (!result.needsPassphrase) {
        // PWK fast path completed. Navigate.
        void navigate({ to: '/app' });
        return;
      }
      // Awaiting passphrase from the user. Clear the stage indicator so the
      // form inputs aren't disabled by `busy`.
      setStage(null);
      setPhase('passphrase');
    } catch (err) {
      reportError(err);
    }
  }, [email, unlock, navigate, reportError]);

  const onPassphraseSubmit = useCallback(async () => {
    setError(null);
    setStage('deriving');
    try {
      await submitPassphrase(passphrase);
      setStage('unlocked');
      void navigate({ to: '/app' });
    } catch (err) {
      reportError(err);
    }
  }, [passphrase, submitPassphrase, navigate, reportError]);

  const onRecoverySubmit = useCallback(async () => {
    setError(null);
    const seed = seedFromCells(seedCells);
    if (!seed) {
      setError(t('unlock.error.all-words'));
      return;
    }
    if (!newPassphraseOk) {
      setError(t('unlock.error.new-passphrase'));
      return;
    }
    setStage('deriving');
    try {
      await submitRecoverySeed(seed, newPassphrase);
      setStage('unlocked');
      void navigate({ to: '/app' });
    } catch (err) {
      reportError(err);
    }
  }, [seedCells, newPassphrase, newPassphraseOk, submitRecoverySeed, navigate, reportError, t]);

  return (
    <section className="mx-auto max-w-md space-y-5 p-8">
      <header className="space-y-3">
        <Wordmark size={20} />
        <div className="flex items-start gap-3">
          <GlobVector mood="lock" size={48} title="Glob" />
          <div>
            <h1 className="font-display text-2xl text-text-1">{t('unlock.title')}</h1>
            <p className="mt-2 text-sm text-text-2">{t('unlock.body')}</p>
          </div>
        </div>
      </header>

      {phase === 'email' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && email.trim()) void startUnlock();
          }}
          className="space-y-4"
        >
          <Field label={t('auth.email')}>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={busy}
              data-testid="unlock-email"
            />
          </Field>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={busy}
            data-testid="unlock-continue-passkey"
          >
            {t('unlock.continue-passkey')}
          </Button>
        </form>
      )}

      {phase === 'passphrase' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && passphrase) void onPassphraseSubmit();
          }}
          className="space-y-4"
        >
          <PassphraseInput
            value={passphrase}
            onChange={setPassphrase}
            label={t('unlock.passphrase-label')}
            autoComplete="current-password"
            autoFocus
            disabled={busy}
          />
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={busy || !passphrase}
            data-testid="unlock-passphrase-submit"
          >
            {t('unlock.unlock')}
          </Button>
          <button
            type="button"
            onClick={() => setPhase('recovery')}
            disabled={busy}
            className="block text-sm text-text-3 underline"
            data-testid="unlock-use-recovery"
          >
            {t('unlock.use-recovery')}
          </button>
        </form>
      )}

      {phase === 'recovery' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void onRecoverySubmit();
          }}
          className="space-y-5"
        >
          <p className="text-sm text-text-2">{t('unlock.recovery.body')}</p>
          <RecoverySeedInput value={seedCells} onChange={setSeedCells} disabled={busy} />
          <div className="space-y-2">
            <p className="text-sm font-medium text-text-1">{t('unlock.recovery.new-passphrase')}</p>
            <PassphraseInput
              value={newPassphrase}
              onChange={setNewPassphrase}
              withStrengthMeter
              withConfirmation
              label={t('unlock.recovery.passphrase-label')}
              disabled={busy}
              onValidityChange={setNewPassphraseOk}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPhase('passphrase')} disabled={busy}>
              {t('auth.back')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={busy || !newPassphraseOk}
              data-testid="unlock-recovery-submit"
            >
              {t('unlock.recover')}
            </Button>
          </div>
        </form>
      )}

      {stage && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border border-border bg-bg-2 px-3 py-2 text-sm text-text-2"
          data-testid="unlock-stage"
        >
          <p>{t(STAGE_KEY[stage])}</p>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-[var(--error)]" data-testid="unlock-error">
          {error}
        </p>
      )}

      <footer className="border-t border-border pt-4 text-xs text-text-3">
        {t('unlock.new-here')}{' '}
        <Link to="/register" className="text-accent underline">
          {t('unlock.create-account')}
        </Link>
        .
      </footer>
    </section>
  );
}

export const Route = createFileRoute('/_public/unlock')({
  component: UnlockFlow,
});
